import { Component, Fragment, type ReactNode } from 'react';

/**
 * 화면 하나가 그리다 죽어도 **앱 전체가 흰 화면이 되지 않게** 한다(10-16 #295 · 07-31 TDZ 와 같은 부류).
 * React 는 렌더 예외를 잡는 곳이 없으면 루트까지 통째로 내린다 — 화면 탭(#168) 하나만 이 카드로 바꾸고
 * 다른 탭·메뉴는 그대로 쓴다. 「다시 열기」= 그 화면만 처음부터(입력은 사라진다) · 「새로고침」= 전체.
 * 🔴 이벤트 핸들러·비동기 오류는 여기로 안 온다(그건 원래 흰 화면을 안 만든다) — 렌더 중 예외만.
 */
type Props = { children: ReactNode; label?: string; variant?: 'staff' | 'driver' };
type State = { error: Error | null; n: number };

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, n: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[ErrorBoundary]', this.props.label ?? '', error, info.componentStack);
  }

  private retry = () => this.setState((s) => ({ error: null, n: s.n + 1 }));

  override render() {
    const { error, n } = this.state;
    // n 을 key 로 — 「다시 열기」 때 안쪽을 처음부터 다시 만든다(같은 상태로 또 죽지 않게).
    if (!error) return <Fragment key={n}>{this.props.children}</Fragment>;
    const driver = this.props.variant === 'driver';
    return (
      <div className={driver ? 'p-5' : 'p-4 sm:p-6'} role="alert">
        <div className="card mx-auto max-w-xl space-y-3 p-5">
          <p className={driver ? 'text-lg font-semibold' : 'font-semibold'}>
            {driver ? '화면을 여는 중 문제가 생겼습니다.' : `${this.props.label ? `「${this.props.label}」 ` : ''}화면을 그리는 중 오류가 났습니다.`}
          </p>
          <p className={driver ? 'text-base text-ink-soft' : 'text-sm text-ink-soft'}>
            {driver
              ? '아래 「다시 열기」를 눌러 주세요. 계속 같으면 사무실에 알려 주세요.'
              : '다른 메뉴는 그대로 쓸 수 있습니다. 「다시 열기」를 누르면 이 화면만 처음부터 다시 엽니다(입력하던 내용은 사라집니다).'}
          </p>
          {!driver && <p className="break-all rounded bg-canvas px-2 py-1 font-mono text-xs text-ink-soft">{error.message || String(error)}</p>}
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={this.retry}>다시 열기</button>
            <button type="button" className="btn" onClick={() => window.location.reload()}>새로고침</button>
          </div>
        </div>
      </div>
    );
  }
}
